export const DEFAULT_AUTH_RETURN_PATH = '/admin';

export const CLIENT_AUTH_RETURN_PATH = '/client';

export const AUTH_RETURN_PATH_STATE_FIELD = 'returnTo';

export const AUTH_RETURN_AUDIENCE_STATE_FIELD = 'returnAudience';

export const CLIENT_AUTH_RETURN_AUDIENCE = 'client';

interface LocationReturnPathParts {
  hash: string;
  pathname: string;
  search: string;
}

const isWithinPrefix = (returnTo: string, allowedPrefix: string) =>
  returnTo === allowedPrefix ||
  returnTo.startsWith(`${allowedPrefix}/`) ||
  returnTo.startsWith(`${allowedPrefix}?`) ||
  returnTo.startsWith(`${allowedPrefix}#`);

export const buildAuthReturnState = (
  returnTo: string,
  returnAudience?: typeof CLIENT_AUTH_RETURN_AUDIENCE
) => {
  const resolvedReturnAudience =
    returnAudience ??
    (isWithinPrefix(returnTo, CLIENT_AUTH_RETURN_PATH)
      ? CLIENT_AUTH_RETURN_AUDIENCE
      : undefined);

  return {
    [AUTH_RETURN_PATH_STATE_FIELD]: returnTo,
    ...(resolvedReturnAudience
      ? { [AUTH_RETURN_AUDIENCE_STATE_FIELD]: resolvedReturnAudience }
      : {}),
  };
};

const getReturnPath = (state: unknown): unknown => {
  if (!state || typeof state !== 'object') {
    return undefined;
  }

  return (state as Record<string, unknown>)[AUTH_RETURN_PATH_STATE_FIELD];
};

export const getAuthReturnAudience = (state: unknown) => {
  if (!state || typeof state !== 'object') {
    return undefined;
  }

  const returnAudience = (state as Record<string, unknown>)[
    AUTH_RETURN_AUDIENCE_STATE_FIELD
  ];

  return returnAudience === CLIENT_AUTH_RETURN_AUDIENCE
    ? CLIENT_AUTH_RETURN_AUDIENCE
    : undefined;
};

export const getSafeAuthReturnPath = (
  state: unknown,
  fallback = DEFAULT_AUTH_RETURN_PATH,
  allowedPrefix?: string
): string => {
  const returnTo = getReturnPath(state);

  if (typeof returnTo !== 'string') {
    return fallback;
  }

  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return fallback;
  }

  if (returnTo.includes('\\')) {
    return fallback;
  }

  if (allowedPrefix && !isWithinPrefix(returnTo, allowedPrefix)) {
    return fallback;
  }

  return returnTo;
};

export const getLocationReturnPath = ({
  hash,
  pathname,
  search,
}: LocationReturnPathParts) => `${pathname}${search}${hash}`;
